import { CacheProvider } from '@emotion/react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes } from 'react-router';

import { useAppearance } from './appearance';
import { useAuth } from './auth/AuthContext';
import AppShell from './components/AppShell';
import { ltrCache, rtlCache } from './emotionCache';
import { api } from './api/client';
import { isRightToLeft, loadPluginTranslations } from './i18n';
import CatalogueTitlePage from './pages/CatalogueTitlePage';
import DashboardPage from './pages/DashboardPage';
import ItemDetailPage from './pages/ItemDetailPage';
import DiscoverPage from './pages/DiscoverPage';
import MoviesPage from './pages/MoviesPage';
import SearchPage from './pages/SearchPage';
import SeriesPage from './pages/SeriesPage';
import LibrariesPage from './pages/LibrariesPage';
import LibrarySettingsPage from './pages/LibrarySettingsPage';
import PreferencesPage from './pages/PreferencesPage';
import LiveTvChannelsPage from './pages/LiveTvChannelsPage';
import LiveTvPage from './pages/LiveTvPage';
import PluginConfigurationPage from './pages/PluginConfigurationPage';
import PluginsPage from './pages/PluginsPage';
import SessionsPage from './pages/SessionsPage';
import SettingsPage from './pages/SettingsPage';
import SetupWizardPage from './pages/SetupWizardPage';
import SignInPage from './pages/SignInPage';
import { createAppTheme } from './theme';

/// Three states, in this order: a server that has never been set up, a visitor who is not
/// signed in, and the application itself.
function Content() {
  const { setup, user, loading } = useAuth();
  const { i18n } = useTranslation();

  // Plugins ship their own translations; they are fetched once there is someone signed in to
  // fetch them for, and again when the language changes. A failure is not fatal - the plugin
  // pages still work, their labels just show as keys until the next attempt succeeds.
  useEffect(() => {
    if (user) {
      void loadPluginTranslations(i18n.language, api.pluginTranslations).catch(() => undefined);
    }
  }, [user, i18n.language]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (setup && !setup.isComplete) {
    return <SetupWizardPage />;
  }

  if (!user) {
    return <SignInPage />;
  }

  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/movies" element={<MoviesPage />} />
        <Route path="/series" element={<SeriesPage />} />
        <Route path="/items/:itemId" element={<ItemDetailPage />} />
        <Route path="/titles/:provider/:kind/:id" element={<CatalogueTitlePage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/discover" element={<DiscoverPage />} />
        <Route path="/libraries" element={<LibrariesPage />} />
        <Route path="/libraries/:libraryId" element={<LibrarySettingsPage />} />
        <Route path="/sessions" element={<SessionsPage />} />
        <Route path="/livetv" element={<LiveTvPage />} />
        <Route path="/livetv/channels/*" element={<LiveTvChannelsPage />} />
        <Route path="/plugins" element={<PluginsPage />} />
        <Route path="/plugins/:pluginId" element={<PluginConfigurationPage />} />
        <Route path="/preferences" element={<PreferencesPage />} />
        <Route path="/settings/*" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </AppShell>
  );
}

export default function App() {
  const { i18n } = useTranslation();
  const rightToLeft = isRightToLeft(i18n.language);
  const direction = rightToLeft ? 'rtl' : 'ltr';

  // Light or dark, and which way the writing runs: the two things the whole interface is
  // built out of, and the only two the theme has to be rebuilt for.
  const { mode } = useAppearance();
  const theme = useMemo(() => createAppTheme(direction, mode), [direction, mode]);

  useEffect(() => {
    // Keeps the document in step with the active locale, which is what screen readers and the
    // browser's own text handling go by.
    document.documentElement.lang = i18n.language;
    document.documentElement.dir = direction;
  }, [i18n.language, direction]);

  useEffect(() => {
    // The ground behind the application - what an overscroll shows, and what the browser
    // paints before the first frame of a reload. The same value the document was given
    // before this script ever ran.
    document.documentElement.style.backgroundColor = theme.palette.background.default;
  }, [theme]);

  return (
    <CacheProvider value={rightToLeft ? rtlCache : ltrCache}>
      <ThemeProvider theme={theme} defaultMode={mode}>
        <CssBaseline />
        <Content />
      </ThemeProvider>
    </CacheProvider>
  );
}
